import React from "react";
import { Paper, CircularProgress, Box, Typography } from "@mui/material";

import { Location as LocationType, Group } from "../../../types";
import { GroupTotal, LocationTotal } from "../../../models";
import { Location } from "../Location";

interface RegularLocationsSectionProps {
  regularLocations: LocationType[];
  groupsByLocationId: Record<number, Group[]>;
  groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
  locationTotalsById: Record<number, LocationTotal>;
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  isLocationSearchHighlighted: (locationId: number) => boolean;
  getSearchMatchingGroupIds: (
    locationId: number,
    groupsByLocationId: Record<number, Group[]>,
  ) => number[];
  onLocationClick: (locationId: number) => void;
  onLocationHoldTap?: (locationId: number) => void;
  isSupervisor?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  hasData?: boolean;
}

export const RegularLocationsSection = ({
  regularLocations,
  groupsByLocationId,
  groupTotalsByLocationId,
  locationTotalsById,
  sourceLocationId,
  destinationLocationId,
  isLocationSearchHighlighted,
  getSearchMatchingGroupIds,
  onLocationClick,
  onLocationHoldTap,
  isSupervisor = false,
  isLoading = false,
  isRefreshing = false,
  hasData = true,
}: RegularLocationsSectionProps) => {
  // Show loader when no regular locations and we're loading (but page has loaded past main LoadingAndEmptyStates)
  const shouldShowLoader =
    regularLocations.length === 0 && (isLoading || isRefreshing);

  return (
    <Paper
      elevation={2}
      sx={{
        height: "calc(50vh - 10px)",
        marginBottom: "20px",
        overflowX: "auto",
        overflowY: "hidden",
        padding: "12px",
      }}
    >
      {shouldShowLoader ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          p={2}
        >
          <CircularProgress />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginTop: "1rem" }}
          >
            Loading regular locations...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            height: "100%",
            minWidth: `${regularLocations.length * 180}px`,
          }}
        >
          {regularLocations.map((location) => {
            const groups = groupsByLocationId[location.LocationID] || [];
            const isSelected =
              sourceLocationId === location.LocationID ||
              destinationLocationId === location.LocationID;

            return (
              <Box
                key={location.LocationID}
                sx={{
                  flex: "1 0 160px",
                  height: "100%",
                  minWidth: "160px",
                }}
              >
                <Location
                  location={location}
                  groups={groups}
                  isSelected={isSelected}
                  isSearchHighlighted={isLocationSearchHighlighted(
                    location.LocationID,
                  )}
                  searchMatchingGroupIds={getSearchMatchingGroupIds(
                    location.LocationID,
                    groupsByLocationId,
                  )}
                  groupTotalsByGroupName={
                    groupTotalsByLocationId[location.LocationID] || {}
                  }
                  locationTotal={locationTotalsById[location.LocationID]}
                  onClick={() => onLocationClick(location.LocationID)}
                  onHoldTap={
                    onLocationHoldTap && (isSupervisor || location.ClearEnabled)
                      ? () => onLocationHoldTap(location.LocationID)
                      : undefined
                  }
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};
